// frontend/src/components/ProjectDetail/panels/JournalEntryForm.js
// --- MODIFIED FILE ---
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css'; 
import { createRoot } from 'react-dom/client';
import { XCircle, Calendar, CheckCircle, Clock, Loader2, FolderOpen, ImagePlus, UploadCloud } from 'lucide-react';
import { apiService } from '../../../services/api';
import JournalTaskCard from './JournalTaskCard';
import DriveBrowserPanel from './DriveBrowserPanel';

// --- Définition du "Blot" personnalisé pour Quill (inchangé) ---
const Embed = Quill.import('blots/embed');
const BlockEmbed = Quill.import('blots/block/embed');

const resolveJournalImageUrl = (value) => {
  const raw = String(value || '');
  if (!raw || /^https?:\/\//i.test(raw)) return raw;
  return `${apiService.getBaseUrl().replace(/\/$/, '')}${raw.startsWith('/') ? raw : `/${raw}`}`;
};

class JournalImageBlot extends BlockEmbed {
  static blotName = 'journal-image';
  static tagName = 'img';
  static className = 'journal-image';

  static create(value) {
    const node = super.create(value);
    const payload = value && typeof value === 'object' ? value : { url: String(value || '') };
    const canonicalUrl = payload.url || payload.src || '';
    const src = resolveJournalImageUrl(canonicalUrl);
    const originalFilename = payload.original_filename || payload.name || 'Image du journal';
    const context = payload.context || '';
    node.setAttribute('src', src);
    node.setAttribute('alt', context || originalFilename);
    node.setAttribute('title', context || originalFilename);
    node.setAttribute('loading', 'lazy');
    const storedPayload = { ...payload, url: canonicalUrl };
    delete storedPayload.src;
    node.setAttribute('data-journal-image', JSON.stringify(storedPayload));
    return node;
  }

  static value(node) {
    const raw = node.getAttribute('data-journal-image');
    if (raw) {
      try {
        return JSON.parse(raw);
      } catch (error) {
        // Fallback on the visible image attributes below.
      }
    }
    return {
      url: node.getAttribute('src') || '',
      src: node.getAttribute('src') || '',
      context: node.getAttribute('alt') || '',
    };
  }
}

if (!Quill.imports['formats/journal-image']) {
  Quill.register(JournalImageBlot, true);
}

class TaskCardBlot extends Embed {
  static blotName = 'task-card';
  static tagName = 'span';
  static className = 'journal-task-card';

  static create(value) {
    const node = super.create(value);
    if (value && typeof value === 'object') {
        node.setAttribute('data-task', JSON.stringify(value));
        const root = createRoot(node);
        root.render(<JournalTaskCard task={value} />);
    }
    return node;
  }

  static value(node) {
    const taskData = node.getAttribute('data-task');
    return taskData ? JSON.parse(taskData) : {};
  }
}

if (Quill.imports['blots/embed'] && !Quill.imports['blots/task-card']) {
    Quill.register(TaskCardBlot, true);
}
// --- Fin de la définition du Blot ---


class StockTagBlot extends Embed {
  static blotName = 'stock-tag';
  static tagName = 'span';
  static className = 'journal-stock-tag';

  static create(value) {
    const node = super.create(value);
    if (value && typeof value === 'object') {
      const stockId = value.stock_id ?? value.stockId ?? '';
      const serialNumber = value.serial_number ?? value.serialNumber ?? '';
      const occurrenceName = value.occurrence_name ?? value.occurrenceName ?? '';
      const baseName = value.item_name ?? value.itemName ?? serialNumber ?? 'Stock';
      const itemName = occurrenceName ? `${baseName} - ${occurrenceName}` : baseName;
      node.setAttribute('data-stock-id', String(stockId));
      node.setAttribute('data-serial-number', String(serialNumber));
      node.setAttribute('data-item-name', String(itemName));
      node.setAttribute('data-occurrence-name', String(occurrenceName));
      node.setAttribute('contenteditable', 'false');
      node.setAttribute('title', serialNumber ? `SN: ${serialNumber}` : '');
      node.textContent = itemName;
      node.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (stockId) {
          window.location.href = `/inventory?stock=${stockId}`;
        }
      });
    }
    return node;
  }

  static value(node) {
    return {
      stock_id: Number(node.getAttribute('data-stock-id')) || null,
      serial_number: node.getAttribute('data-serial-number') || '',
      item_name: node.getAttribute('data-item-name') || '',
      occurrence_name: node.getAttribute('data-occurrence-name') || '',
    };
  }
}

if (Quill.imports['blots/embed'] && !Quill.imports['blots/stock-tag']) {
  Quill.register(StockTagBlot, true);
}

class DriveRefBlot extends Embed {
  static blotName = 'drive-ref';
  static tagName = 'span';
  static className = 'journal-drive-ref';

  static create(value) {
    const node = super.create(value);
    if (value && typeof value === 'object') {
      const payload = {
        drive_id: value.drive_id || value.id || '',
        name: value.name || 'Drive',
        mime_type: value.mime_type || '',
        is_folder: Boolean(value.is_folder),
        web_view_link: value.web_view_link || '',
      };
      node.setAttribute('data-drive-ref', JSON.stringify(payload));
      node.setAttribute('contenteditable', 'false');
      node.setAttribute('title', payload.web_view_link || payload.name);
      node.textContent = `${payload.is_folder ? 'Dossier' : 'Fichier'} Drive: ${payload.name}`;
      node.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (payload.web_view_link) {
          window.open(payload.web_view_link, '_blank', 'noopener,noreferrer');
        }
      });
    }
    return node;
  }

  static value(node) {
    const raw = node.getAttribute('data-drive-ref');
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch (error) {
      return {};
    }
  }
}

if (Quill.imports['blots/embed'] && !Quill.imports['blots/drive-ref']) {
  Quill.register(DriveRefBlot, true);
}

const JournalEntryForm = ({ initialData, onSubmit, onCancel, projectId, project, employees, isSaving }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [saveStatus, setSaveStatus] = useState('saved'); 
  const autoSaveTimeoutRef = useRef(null);
  
  const quillRef = useRef(null);
  const [mention, setMention] = useState({ show: false, top: 0, left: 0, query: '', type: 'employee' });
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [stockMentionSuggestions, setStockMentionSuggestions] = useState([]);
  const [stockMentionLoading, setStockMentionLoading] = useState(false);
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const [imageUploadCount, setImageUploadCount] = useState(0);
  const [imageUploadError, setImageUploadError] = useState('');
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  const imageInputRef = useRef(null);
  const imageUploadHandlerRef = useRef(null);
  const imageUploadBusyRef = useRef(false);
  const dragDepthRef = useRef(0);
  const stockMentionRequestRef = useRef(0);
  const stockMentionQueryRef = useRef('');
  const mentionStartIndexRef = useRef(null);
  const stockLogSyncRef = useRef(new Set());
  
  const enterKeyHandlerRef = useRef(null);
  const tabKeyHandlerRef = useRef(null);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      try {
        const parsedContent = JSON.parse(initialData.content);
        setContent(parsedContent);
      } catch (e) {
        setContent(initialData.content || '');
      }
      
      let formattedDate;
      if (initialData.entry_date instanceof Date) {
          formattedDate = initialData.entry_date.toISOString().split('T')[0];
      } else if (typeof initialData.entry_date === 'string') {
          try {
            formattedDate = new Date(initialData.entry_date).toISOString().split('T')[0];
          } catch (e) {
            formattedDate = new Date().toISOString().split('T')[0];
          }
      } else {
          formattedDate = new Date().toISOString().split('T')[0];
      }
      setEntryDate(formattedDate);
      setSaveStatus('saved');
    } else {
        setTitle('');
        setContent('');
        setEntryDate(new Date().toISOString().split('T')[0]);
        setSaveStatus('saved');
    }
  }, [initialData?.id]);

  useEffect(() => {
    return () => {
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }
    };
  }, []);

  const extractStockMentionsFromDelta = useCallback((delta) => {
    const ops = Array.isArray(delta?.ops) ? delta.ops : [];
    const results = [];
    let lineText = '';
    let lineTags = [];

    const flushLine = () => {
      if (lineTags.length > 0) {
        const cleaned = lineText.trim();
        lineTags.forEach((tag) => {
          results.push({ ...tag, lineText: cleaned });
        });
      }
      lineText = '';
      lineTags = [];
    };

    ops.forEach((op) => {
      if (typeof op.insert === 'string') {
        const segments = op.insert.split('\n');
        segments.forEach((segment, index) => {
          if (index > 0) {
            flushLine();
          }
          lineText += segment;
        });
        return;
      }
      if (op && typeof op.insert === 'object' && op.insert['stock-tag']) {
        const tag = op.insert['stock-tag'];
        const stockId = tag.stock_id ?? tag.stockId ?? null;
        const serialNumber = tag.serial_number ?? tag.serialNumber ?? '';
        const itemName = tag.item_name ?? tag.itemName ?? serialNumber ?? '';
        lineTags.push({ stockId, serialNumber, itemName });
        lineText += itemName || serialNumber || '';
        return;
      }
      if (op && typeof op.insert === 'object') {
        lineText += ' ';
      }
    });

    flushLine();
    return results;
  }, []);

  const syncStockMentionsToLogs = useCallback(
    async (entryId, delta) => {
      if (!entryId || !projectId || !delta) return;
      const mentions = extractStockMentionsFromDelta(delta);
      if (!mentions.length) return;
      const tasks = mentions
        .map((mentionItem) => {
          if (!mentionItem.stockId) return null;
          const baseLine = mentionItem.lineText || mentionItem.itemName || mentionItem.serialNumber || '';
          const suffix = mentionItem.serialNumber ? ` (SN ${mentionItem.serialNumber})` : '';
          const message = `Journal projet: ${baseLine || 'Mention stock'}${suffix} [[PROJECT_JOURNAL:${projectId}:${entryId}]]`;
          const key = `${entryId}-${mentionItem.stockId}-${baseLine}-${mentionItem.serialNumber}`;
          if (stockLogSyncRef.current.has(key)) return null;
          stockLogSyncRef.current.add(key);
          return apiService.post(`/inventory/stock/${mentionItem.stockId}/logs`, { message })
            .catch((error) => {
              console.error('Erreur ajout journal stock:', error);
            });
        })
        .filter(Boolean);
      await Promise.all(tasks);
    },
    [projectId, extractStockMentionsFromDelta],
  );

  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
    }
    setSaveStatus('saving');
    
    autoSaveTimeoutRef.current = setTimeout(async () => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            const contentDelta = quill.getContents();

            const entryPayload = {
                project_id: projectId,
                title: title.trim() || `Entrée du ${new Date(entryDate).toLocaleDateString('fr-FR')}`,
                content: JSON.stringify(contentDelta),
                entry_date: entryDate,
                tags: [],
            };
            const savedEntry = await onSubmit(entryPayload, initialData?.id);
            if (savedEntry?.id) {
                await syncStockMentionsToLogs(savedEntry.id, contentDelta);
            }
            setSaveStatus('saved');
        }
    }, 1500);
  }, [title, entryDate, onSubmit, initialData?.id, projectId, syncStockMentionsToLogs]);


  const handleFinalSaveAndClose = useCallback(async () => {
    if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
    }
    
    if (saveStatus === 'typing' || saveStatus === 'saving') {
        setSaveStatus('saving');
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            const contentDelta = quill.getContents();
            const entryPayload = {
                project_id: projectId,
                title: title.trim() || `Entrée du ${new Date(entryDate).toLocaleDateString('fr-FR')}`,
                content: JSON.stringify(contentDelta),
                entry_date: entryDate,
                tags: [],
            };
            const savedEntry = await onSubmit(entryPayload, initialData?.id);
            if (savedEntry?.id) {
                await syncStockMentionsToLogs(savedEntry.id, contentDelta);
            }
        }
    }
    setSaveStatus('saved');
    onCancel();
  }, [projectId, title, entryDate, onSubmit, initialData?.id, onCancel, saveStatus, syncStockMentionsToLogs]);


  const handleContentChange = (newContent, delta, source, editor) => {
    if (source === 'user') {
      setContent(newContent);
      setSaveStatus('typing');
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = setTimeout(triggerAutoSave, 2000);
    }
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setSaveStatus('typing');
  };

  useEffect(() => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();

    const handleTextChange = (delta, oldDelta, source) => {
      if (source !== 'user') return;
      const selection = quill.getSelection();
      if (!selection || selection.length > 0) {
        setMention({ show: false, top: 0, left: 0, query: '', type: 'employee' });
        return;
      }
      const sliceStart = Math.max(0, selection.index - 50);
      const recentText = quill.getText(sliceStart, selection.index - sliceStart);
      const mentionMatch = recentText.match(/@([^\s]*)$/);
      if (mentionMatch) {
          const queryRaw = mentionMatch[1];
          const queryLower = queryRaw.toLowerCase();
          mentionStartIndexRef.current = selection.index - queryRaw.length - 1;
          const bounds = quill.getBounds(mentionStartIndexRef.current);
          const isStockMention =
            queryLower.startsWith('sn') ||
            queryLower.startsWith('inv') ||
            /^\d/.test(queryLower);
          setMention({ show: true, top: bounds.top, left: bounds.left, query: queryRaw, type: isStockMention ? 'stock' : 'employee' });
          if (isStockMention) {
            let searchValue = queryLower;
            if (queryLower.startsWith('sn')) {
              searchValue = queryLower.replace(/^sn[:-]?/, '').trim();
            }
            if (!searchValue) {
              setStockMentionSuggestions([]);
              setStockMentionLoading(false);
              stockMentionQueryRef.current = '';
              return;
            }
            if (stockMentionQueryRef.current !== searchValue) {
              stockMentionQueryRef.current = searchValue;
              const requestId = ++stockMentionRequestRef.current;
              setStockMentionLoading(true);
              apiService.searchStockSerials(searchValue, 8)
                .then((data) => {
                  if (requestId !== stockMentionRequestRef.current) return;
                  setStockMentionSuggestions(Array.isArray(data) ? data : []);
                })
                .catch((error) => {
                  if (requestId !== stockMentionRequestRef.current) return;
                  console.error('Erreur recherche serial:', error);
                  setStockMentionSuggestions([]);
                })
                .finally(() => {
                  if (requestId === stockMentionRequestRef.current) {
                    setStockMentionLoading(false);
                  }
                });
            }
          } else {
            setMentionSuggestions(employees.filter(emp => emp.name.toLowerCase().includes(queryLower)));
          }
      } else {
          setMention({ show: false, top: 0, left: 0, query: '', type: 'employee' });
          setStockMentionSuggestions([]);
          setStockMentionLoading(false);
          stockMentionQueryRef.current = '';
      }
    };
    quill.on('text-change', handleTextChange);
    return () => quill.off('text-change', handleTextChange);
  }, [employees]);

  const handleSelectMention = useCallback((employee) => {
    const quill = quillRef.current.getEditor();
    const mentionLength = mention.query.length + 1;
    const startIndex = mentionStartIndexRef.current;
    quill.deleteText(startIndex, mentionLength, 'user');
    quill.insertText(startIndex, `@${employee.name}`, { 'bold': true, 'color': '#2563eb' }, 'user');
    const endOfNameIndex = startIndex + employee.name.length + 1;
    quill.insertText(endOfNameIndex, ' ', 'user');
    quill.setSelection(endOfNameIndex + 1, 0, 'user');
    quill.format('color', '#6b7280');
    quill.format('italic', true);
    setMention({ show: false, top: 0, left: 0, query: '', type: 'employee' });
  }, [mention.query]);

  const handleSelectStockMention = useCallback((stockItem) => {
    const quill = quillRef.current.getEditor();
    const mentionLength = mention.query.length + 1;
    const startIndex = mentionStartIndexRef.current;
    quill.deleteText(startIndex, mentionLength, 'user');
    quill.insertEmbed(startIndex, 'stock-tag', {
      stock_id: stockItem.id,
      serial_number: stockItem.serial_number,
      item_name: stockItem.item_name || stockItem.serial_number,
      occurrence_name: stockItem.occurrence_name || '',
    }, 'user');
    const endIndex = startIndex + 1;
    quill.insertText(endIndex, ' ', 'user');
    quill.setSelection(endIndex + 1, 0, 'user');
    setMention({ show: false, top: 0, left: 0, query: '', type: 'employee' });
  }, [mention.query]);

  const handleSelectDriveReference = useCallback((driveItem) => {
    if (!quillRef.current || !driveItem) return;
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection(true);
    const insertIndex = range ? range.index : Math.max(0, quill.getLength() - 1);
    quill.insertEmbed(insertIndex, 'drive-ref', driveItem, 'user');
    quill.insertText(insertIndex + 1, ' ', 'user');
    quill.setSelection(insertIndex + 2, 0, 'user');
    setSaveStatus('typing');
    setDrivePickerOpen(false);
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(triggerAutoSave, 1200);
  }, [triggerAutoSave]);

  const imageContextAt = useCallback((quill, index) => {
    const start = Math.max(0, index - 500);
    const nearby = quill.getText(start, 1000).replace(/\s+/g, ' ').trim();
    const entryLabel = title.trim() || 'Entree de journal';
    return `${entryLabel} (${entryDate})${nearby ? ` - ${nearby}` : ''}`.slice(0, 1800);
  }, [title, entryDate]);

  const uploadJournalImages = useCallback(async (fileList, requestedIndex = null) => {
    if (!quillRef.current) return;
    const files = Array.from(fileList || []).filter((file) => file && (
      file.type?.startsWith('image/') || /\.(?:jpe?g|png|webp)$/i.test(file.name || '')
    ));
    if (!files.length) return;
    if (imageUploadBusyRef.current) {
      setImageUploadError("Un ajout d'images est deja en cours.");
      return;
    }
    imageUploadBusyRef.current = true;

    const quill = quillRef.current.getEditor();
    const selection = quill.getSelection(true);
    let insertIndex = Number.isInteger(requestedIndex)
      ? requestedIndex
      : (selection?.index ?? Math.max(0, quill.getLength() - 1));
    setImageUploadError('');
    setImageUploadCount(files.length);

    try {
      for (const file of files) {
        try {
          if (file.size > 20 * 1024 * 1024) {
            throw new Error(`${file.name || 'Image'} depasse la limite de 20 Mo.`);
          }
          const formData = new FormData();
          formData.append('file', file, file.name || 'image');
          formData.append('context', imageContextAt(quill, insertIndex));
          if (initialData?.id) formData.append('entry_id', String(initialData.id));
          const uploaded = await apiService.uploadFile(`/projects/${projectId}/journal/images`, formData);
          const payload = {
            ...uploaded,
            url: uploaded?.url || '',
            name: uploaded?.original_filename || file.name || 'Image du journal',
          };
          quill.insertEmbed(insertIndex, 'journal-image', payload, 'user');
          quill.insertText(insertIndex + 1, '\n', 'user');
          insertIndex += 2;
          quill.setSelection(insertIndex, 0, 'silent');
        } catch (error) {
          console.error('Erreur upload image du journal:', error);
          setImageUploadError(error?.message || "Impossible d'ajouter cette image.");
        } finally {
          setImageUploadCount((count) => Math.max(0, count - 1));
        }
      }
      quill.focus();
    } finally {
      imageUploadBusyRef.current = false;
    }
  }, [projectId, initialData?.id, imageContextAt]);

  const openImagePicker = useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  useEffect(() => {
    imageUploadHandlerRef.current = openImagePicker;
  }, [openImagePicker]);

  useEffect(() => {
    if (!quillRef.current) return undefined;
    const quill = quillRef.current.getEditor();
    const editorRoot = quill.root;

    const imageFilesFrom = (dataTransfer) => Array.from(dataTransfer?.files || [])
      .filter((file) => file.type?.startsWith('image/') || /\.(?:jpe?g|png|webp)$/i.test(file.name || ''));
    const dropIndexAt = (event) => {
      const domRange = document.caretRangeFromPoint?.(event.clientX, event.clientY);
      if (!domRange || !editorRoot.contains(domRange.startContainer)) {
        return quill.getSelection(true)?.index ?? Math.max(0, quill.getLength() - 1);
      }
      const blot = Quill.find(domRange.startContainer, true);
      if (!blot) return quill.getSelection(true)?.index ?? Math.max(0, quill.getLength() - 1);
      const offset = domRange.startContainer.nodeType === Node.TEXT_NODE ? domRange.startOffset : 0;
      return Math.min(Math.max(0, quill.getIndex(blot) + offset), Math.max(0, quill.getLength() - 1));
    };

    const handlePaste = (event) => {
      const files = imageFilesFrom(event.clipboardData);
      if (!files.length) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const index = quill.getSelection(true)?.index ?? Math.max(0, quill.getLength() - 1);
      void uploadJournalImages(files, index);
    };

    const handleDragEnter = (event) => {
      if (!imageFilesFrom(event.dataTransfer).length && !Array.from(event.dataTransfer?.items || [])
        .some((item) => item.kind === 'file' && item.type.startsWith('image/'))) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDraggingImages(true);
    };
    const handleDragOver = (event) => {
      const hasImage = Array.from(event.dataTransfer?.items || [])
        .some((item) => item.kind === 'file' && item.type.startsWith('image/'));
      if (!hasImage) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    };
    const handleDragLeave = (event) => {
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDraggingImages(false);
    };
    const handleDrop = (event) => {
      const files = imageFilesFrom(event.dataTransfer);
      if (!files.length) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      dragDepthRef.current = 0;
      setIsDraggingImages(false);
      const index = dropIndexAt(event);
      void uploadJournalImages(files, index);
    };

    editorRoot.addEventListener('paste', handlePaste, true);
    editorRoot.addEventListener('dragenter', handleDragEnter, true);
    editorRoot.addEventListener('dragover', handleDragOver, true);
    editorRoot.addEventListener('dragleave', handleDragLeave, true);
    editorRoot.addEventListener('drop', handleDrop, true);
    return () => {
      editorRoot.removeEventListener('paste', handlePaste, true);
      editorRoot.removeEventListener('dragenter', handleDragEnter, true);
      editorRoot.removeEventListener('dragover', handleDragOver, true);
      editorRoot.removeEventListener('dragleave', handleDragLeave, true);
      editorRoot.removeEventListener('drop', handleDrop, true);
    };
  }, [uploadJournalImages]);

  const createTaskFromLine = useCallback(async (employee, taskName, index) => {
      const quill = quillRef.current.getEditor();
      try {
          const projectName = project ? project.name : 'Projet';
          const formattedTaskName = `[${projectName}] ${taskName}`;
          const createdTask = await apiService.createTask({ name: formattedTaskName, project_id: projectId, owner_id: employee.id, });
          const createdTaskWithDetails = { ...createdTask, owner_name: employee.name, owner_email: employee.email };
          quill.insertEmbed(index, 'task-card', createdTaskWithDetails, 'user');
          quill.insertText(index + 1, '\n', 'user');
          quill.setSelection(index + 2, 0, 'user');
      } catch (error) {
          console.error("Erreur création de tâche depuis le journal:", error);
          quill.insertText(index, `@${employee.name} ${taskName}\n`, 'user');
      }
  }, [projectId, project]);
  
  const handleEnterKeyLogic = useCallback((range, context) => {
      if (mention.show) {
          if (mention.type === 'stock' && stockMentionSuggestions.length > 0) {
              handleSelectStockMention(stockMentionSuggestions[0]);
              return false;
          }
          if (mention.type !== 'stock' && mentionSuggestions.length > 0) {
              handleSelectMention(mentionSuggestions[0]);
              return false;
          }
      }
      const quill = quillRef.current.getEditor();
      const [line, offset] = quill.getLine(range.index);
      const lineText = line.domNode.textContent || '';
      const lineStart = range.index - offset;
      if (lineText.startsWith('@')) {
          for (const employee of employees) {
              const mentionTrigger = `@${employee.name} `;
              if (lineText.startsWith(mentionTrigger)) {
                  const taskName = lineText.substring(mentionTrigger.length).trim();
                  if (taskName) {
                      quill.deleteText(lineStart, line.length());
                      createTaskFromLine(employee, taskName, lineStart);
                      return false;
                  }
              }
          }
      }
      return true;
  }, [mention.show, mention.type, mentionSuggestions, stockMentionSuggestions, employees, handleSelectMention, handleSelectStockMention, createTaskFromLine]);
  
  const handleTabKeyLogic = useCallback((range, context) => {
    if (mention.show) {
      if (mention.type === 'stock' && stockMentionSuggestions.length > 0) {
        handleSelectStockMention(stockMentionSuggestions[0]);
        return false;
      }
      if (mention.type !== 'stock' && mentionSuggestions.length > 0) {
        handleSelectMention(mentionSuggestions[0]);
        return false;
      }
    }
    return true;
  }, [mention.show, mention.type, mentionSuggestions, stockMentionSuggestions, handleSelectMention, handleSelectStockMention]);
  
  useEffect(() => {
      enterKeyHandlerRef.current = handleEnterKeyLogic;
      tabKeyHandlerRef.current = handleTabKeyLogic;
  }, [handleEnterKeyLogic, handleTabKeyLogic]);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline'],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: () => imageUploadHandlerRef.current?.(),
      },
    },
    keyboard: {
        bindings: {
            enter: { key: 13, handler: (range, context) => enterKeyHandlerRef.current ? enterKeyHandlerRef.current(range, context) : true },
            tab: { key: 9, handler: (range, context) => tabKeyHandlerRef.current ? tabKeyHandlerRef.current(range, context) : true }
        }
    }
  }), []); 

  const renderSaveStatusIcon = () => {
    switch(saveStatus) {
        case 'typing':
            return <div title="Modifications en attente" className="flex items-center text-gray-500"><Clock size={14} /></div>;
        case 'saving':
            return <div title="Sauvegarde en cours..." className="flex items-center text-blue-600"><Loader2 size={14} className="animate-spin" /></div>;
        case 'saved':
            return <div title="Enregistré" className="flex items-center text-green-600"><CheckCircle size={14} /></div>;
        default:
            return null;
    }
  };

  return (
    <div className="space-y-3 bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 mb-2">
        <div className="flex-grow">
          <label htmlFor="journal-title-compact" className="sr-only">Titre</label>
          <input id="journal-title-compact" type="text" value={title} onChange={handleTitleChange} onBlur={triggerAutoSave} required disabled={isSaving}
            className="w-full px-3 py-2 text-lg font-semibold border-b border-gray-300 focus:outline-none focus:border-blue-500 placeholder-gray-400"
            placeholder="Titre de l'entrée..."
          />
        </div>
        <div className="relative">
            <Calendar size={14} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input id="journal-date-compact" type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} onBlur={triggerAutoSave} required disabled={isSaving}
                className="w-full sm:w-auto pl-7 pr-2 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
            />
        </div>
        <button
          type="button"
          onClick={openImagePicker}
          disabled={isSaving || imageUploadCount > 0}
          className="inline-flex items-center justify-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <ImagePlus size={14} />
          Image
        </button>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(event) => {
            void uploadJournalImages(event.target.files);
            event.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => setDrivePickerOpen(true)}
          disabled={isSaving}
          className="inline-flex items-center justify-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <FolderOpen size={14} />
          Drive
        </button>
      </div>
      
      {/* ✅ FIX: Utilisation de `pointer-events-none` au lieu de `readOnly` */}
      <div className={`relative journal-quill-editor-expanded flex-grow flex flex-col min-h-[300px] ${isSaving ? "opacity-70 pointer-events-none" : ""}`}>
        <label className="sr-only">Contenu</label>
        <ReactQuill 
          ref={quillRef} 
          theme="snow" 
          value={content} 
          onChange={handleContentChange} 
          modules={quillModules} 
          // ❌ REMOVE: La prop readOnly est supprimée pour ne plus perturber l'éditeur
          className="flex-grow h-full" 
          style={{ display: 'flex', flexDirection: 'column' }}
        />
        {isDraggingImages && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-md border-2 border-dashed border-blue-500 bg-blue-50/90 text-blue-700">
            <div className="flex flex-col items-center gap-2 font-medium">
              <UploadCloud size={30} />
              Deposez les images ici
            </div>
          </div>
        )}
        {imageUploadCount > 0 && (
          <div className="absolute bottom-3 right-3 z-20 flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-xs text-white shadow-lg">
            <Loader2 size={14} className="animate-spin" />
            Upload de {imageUploadCount} image{imageUploadCount > 1 ? 's' : ''}...
          </div>
        )}
        {mention.show && mention.type !== 'stock' && mentionSuggestions.length > 0 && (
          <div className="absolute bg-white border rounded shadow-lg z-10" style={{ top: mention.top, left: mention.left, transform: 'translateY(-100%) translateY(-2px)' }}>
            {mentionSuggestions.slice(0, 5).map(emp => (
              <div key={emp.id} className="px-3 py-1.5 cursor-pointer hover:bg-gray-100 text-sm"
                onMouseDown={(e) => { e.preventDefault(); handleSelectMention(emp); }}>
                {emp.name}
              </div>
            ))}
          </div>
        )}
        {mention.show && mention.type === 'stock' && (
          <div className="absolute bg-white border rounded shadow-lg z-10" style={{ top: mention.top, left: mention.left, transform: 'translateY(-100%) translateY(-2px)' }}>
            {stockMentionLoading && (
              <div className="px-3 py-1.5 text-xs text-gray-500">Recherche...</div>
            )}
            {stockMentionSuggestions.slice(0, 6).map((item) => (
              <div
                key={item.id}
                className="px-3 py-1.5 cursor-pointer hover:bg-gray-100 text-sm flex flex-col"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectStockMention(item);
                }}
              >
                <span className="font-medium text-gray-800">
                  {item.item_name || item.serial_number}
                  {item.occurrence_name ? ` - ${item.occurrence_name}` : ''}
                </span>
                {item.serial_number && (
                  <span className="text-[11px] text-gray-500">{item.serial_number}</span>
                )}
              </div>
            ))}
            {!stockMentionLoading && stockMentionSuggestions.length === 0 && (
              <div className="px-3 py-1.5 text-xs text-gray-500">
                Tapez un serial (ex: @INV-000123)
              </div>
            )}
          </div>
        )}
      </div>

      {imageUploadError && (
        <div role="alert" className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <span>{imageUploadError}</span>
          <button type="button" onClick={() => setImageUploadError('')} aria-label="Fermer l'erreur image">
            <XCircle size={15} />
          </button>
        </div>
      )}

      {drivePickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="flex max-h-[88vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-800">Reference Drive</h3>
              <button
                type="button"
                onClick={() => setDrivePickerOpen(false)}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                title="Fermer"
              >
                <XCircle size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden p-4">
              <DriveBrowserPanel
                projectId={projectId}
                project={project}
                selectable
                onSelect={handleSelectDriveReference}
                maxHeight="54vh"
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center pt-3 border-t mt-auto">
        <div className="flex items-center space-x-2">
            {renderSaveStatusIcon()}
        </div>
        <div className="flex justify-end space-x-3">
          <button type="button" onClick={handleFinalSaveAndClose} disabled={isSaving || imageUploadCount > 0}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center">
            <XCircle size={16} className="inline mr-1" /> Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default JournalEntryForm;
